// de-DE formatting and Europe/Berlin dates (architecture §5.6, §21.4). Pure, no DOM, no Node APIs.
export const TZ = 'Europe/Berlin';

const fmtCache = new Map<string, Intl.DateTimeFormat>();
function dtf(key: string, opts: Intl.DateTimeFormatOptions): Intl.DateTimeFormat {
  let f = fmtCache.get(key);
  if (!f) { f = new Intl.DateTimeFormat('de-DE', { timeZone: TZ, ...opts }); fmtCache.set(key, f); }
  return f;
}

function toDate(d: Date | string | number): Date {
  return d instanceof Date ? d : new Date(d);
}

export interface BerlinParts { year: number; month: number; day: number; hour: number; minute: number; second: number; weekday: number /* 1=Mo..7=So */ }

/** Calendar parts of an instant in Europe/Berlin. */
export function berlinParts(date: Date | string | number): BerlinParts {
  const parts = dtf('parts', {
    year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23', weekday: 'short',
  }).formatToParts(toDate(date));
  const get = (t: Intl.DateTimeFormatPartTypes) => parts.find((p) => p.type === t)?.value ?? '';
  const wd = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'].indexOf(get('weekday').replace('.', '').slice(0, 2)) + 1;
  return {
    year: Number(get('year')), month: Number(get('month')), day: Number(get('day')),
    hour: Number(get('hour')), minute: Number(get('minute')), second: Number(get('second')), weekday: wd,
  };
}

/** Europe/Berlin calendar day as `YYYY-MM-DD` (business days, `date` columns). */
export function berlinDay(date: Date | string | number = new Date()): string {
  const p = berlinParts(date);
  return `${p.year}-${String(p.month).padStart(2, '0')}-${String(p.day).padStart(2, '0')}`;
}

export type BerlinFormat = 'datum' | 'zeit' | 'datumzeit' | 'wochentag' | 'datum_kurz';

/** "07.10.2026", "10:00", "07.10.2026, 10:00", "Mi 07.10.2026", "07.10." */
export function formatBerlin(date: Date | string | number, kind: BerlinFormat = 'datumzeit'): string {
  const d = toDate(date);
  const datum = dtf('datum', { day: '2-digit', month: '2-digit', year: 'numeric' }).format(d);
  const zeit = dtf('zeit', { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).format(d);
  switch (kind) {
    case 'datum': return datum;
    case 'zeit': return zeit;
    case 'datumzeit': return `${datum}, ${zeit}`;
    case 'datum_kurz': return datum.slice(0, 6);
    case 'wochentag': {
      const wd = dtf('wd', { weekday: 'short' }).format(d).replace('.', '');
      return `${wd} ${datum}`;
    }
  }
}

const numCache = new Map<string, Intl.NumberFormat>();
function nf(key: string, opts: Intl.NumberFormatOptions): Intl.NumberFormat {
  let f = numCache.get(key);
  if (!f) { f = new Intl.NumberFormat('de-DE', opts); numCache.set(key, f); }
  return f;
}

/** "1.234,50 €" */
export function formatEuro(value: number, decimals = 2): string {
  return nf(`eur${decimals}`, { style: 'currency', currency: 'EUR', minimumFractionDigits: decimals, maximumFractionDigits: decimals }).format(value);
}

/** Amount in any ISO currency, e.g. "74,90 €" or "12,00 US$". */
export function formatMoney(value: number, currency: string): string {
  try {
    return nf(`cur${currency}`, { style: 'currency', currency }).format(value);
  } catch {
    return `${formatNumber(value, 2)} ${currency}`;
  }
}

/** "1.234" or "1.234,56" */
export function formatNumber(value: number, decimals = 0): string {
  return nf(`n${decimals}`, { minimumFractionDigits: decimals, maximumFractionDigits: decimals }).format(value);
}

/** ratio 0.823 → "82 %" */
export function formatPercent(ratio: number, decimals = 0): string {
  return nf(`p${decimals}`, { style: 'percent', minimumFractionDigits: decimals, maximumFractionDigits: decimals }).format(ratio);
}

/** "gerade eben", "vor 5 Min.", "vor 2 Std.", "gestern", "vor 3 Tagen", else "07.10.2026". */
export function formatRelative(date: Date | string | number, now: Date | number = Date.now()): string {
  const d = toDate(date);
  const nowMs = typeof now === 'number' ? now : now.getTime();
  const diffS = Math.round((nowMs - d.getTime()) / 1000);
  if (diffS < 0) {
    const ahead = -diffS;
    if (ahead < 60) return 'gleich';
    if (ahead < 3600) return `in ${Math.round(ahead / 60)} Min.`;
    if (ahead < 86_400) return `in ${Math.round(ahead / 3600)} Std.`;
    return formatBerlin(d, 'datum');
  }
  if (diffS < 45) return 'gerade eben';
  if (diffS < 3600) return `vor ${Math.max(1, Math.round(diffS / 60))} Min.`;
  const today = berlinDay(nowMs);
  const day = berlinDay(d);
  if (day === today) return `vor ${Math.round(diffS / 3600)} Std.`;
  // Calendar days in Berlin: "now minus 24 hours" missed yesterday around the clock changes (days of 23 and 25 hours).
  const days = Math.round((Date.parse(today) - Date.parse(day)) / 86_400_000);
  if (days === 1) return 'gestern';
  if (days < 7) return `vor ${days} Tagen`;
  return formatBerlin(d, 'datum');
}

/**
 * Parses German amounts: "74,90", "74.90", "74,90 €", "€ 74,90", "EUR 1.234,50", "1.234", "-5,00". Anything else is null,
 * never a misread (WP5 round 4): this function read "1,234.50", "0x10" and "1e3" as 1.2345, 16 and 1000, and the light
 * import, which reads its amounts here since round 4, kept every digit, dot and comma ("12 Stück à 3,50" became 123.5).
 * A dot followed by groups of three digits separates thousands ("1.234" is 1234); with one or two digits it is the decimal
 * point. A first group that starts with 0 is no thousands group (WP5 round 5): "0.500" was 500, an English 0.5 a thousand
 * times larger; it is null now, as "1,234.50" is. At most 40 characters, so no input costs more than a constant.
 */
export function parseDe(input: string): number | null {
  if (input.length > 40) return null;
  let s = input.trim();
  const upper = s.toUpperCase();
  if (s.startsWith('€')) s = s.slice(1).trimStart();
  else if (upper.startsWith('EUR')) s = s.slice(3).trimStart();
  else if (s.endsWith('€')) s = s.slice(0, -1).trimEnd();
  else if (upper.endsWith('EUR')) s = s.slice(0, -3).trimEnd();
  const neg = s.startsWith('-');
  const body = neg ? s.slice(1) : s;
  let n: number;
  if (/^[1-9]\d{0,2}(?:\.\d{3})+(?:,\d+)?$/.test(body)) n = Number(body.replace(/\./g, '').replace(',', '.'));
  else if (/^\d+(?:,\d+)?$/.test(body)) n = Number(body.replace(',', '.'));
  else if (/^\d+\.\d{1,2}$/.test(body)) n = Number(body);
  else return null;
  return Number.isFinite(n) ? (neg ? -n : n) : null;
}
